import { forwardRef } from 'react';
import type { InputHTMLAttributes, TextareaHTMLAttributes, ReactNode } from 'react';
import { Search as SearchIcon, X } from 'lucide-react';
import { cn } from './cn';

export type ControlSize = 'sm' | 'md' | 'lg';

const SIZE: Record<ControlSize, string> = {
  sm: 'h-8 text-[13px]',
  md: 'h-9 text-sm',
  lg: 'h-11 text-[15px]',
};

/** Accept both the ui sizes and antd's small/middle/large aliases. */
type AnySize = ControlSize | 'small' | 'middle' | 'large';
const normSize = (s?: AnySize): ControlSize => (s === 'small' ? 'sm' : s === 'middle' ? 'md' : s === 'large' ? 'lg' : (s as ControlSize) ?? 'md');

const shell = (invalid?: boolean) =>
  cn(
    'rounded-control border bg-surface transition-colors',
    invalid ? 'border-danger-500' : 'border-line',
    'focus-within:ring-2 focus-within:ring-brand-500/30',
    invalid ? 'focus-within:border-danger-500' : 'focus-within:border-brand-400',
  );

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'> {
  size?: AnySize;
  invalid?: boolean;
  prefix?: ReactNode;
  suffix?: ReactNode;
  /** Accepted for antd compatibility (no-op — the ui Input has no built-in clear button). */
  allowClear?: boolean;
  /** antd status → maps to the invalid ring. */
  status?: 'error' | 'warning';
}

/** Text input (replaces antd Input), with optional prefix/suffix slots. */
const InputBase = forwardRef<HTMLInputElement, InputProps>(function Input(
  { size, invalid, prefix, suffix, className, allowClear, status, ...rest },
  ref,
) {
  void allowClear;
  const sz = normSize(size);
  invalid = invalid || status === 'error';
  if (prefix != null || suffix != null) {
    return (
      <div className={cn('flex items-center gap-2 px-3', SIZE[sz], shell(invalid), className)}>
        {prefix != null && <span className="shrink-0 text-muted [&>svg]:size-4">{prefix}</span>}
        <input
          ref={ref}
          className="min-w-0 flex-1 bg-transparent text-content placeholder:text-subtle focus:outline-none"
          {...rest}
        />
        {suffix != null && <span className="shrink-0 text-muted [&>svg]:size-4">{suffix}</span>}
      </div>
    );
  }
  return (
    <input
      ref={ref}
      className={cn('w-full px-3 text-content placeholder:text-subtle focus:outline-none', SIZE[sz], shell(invalid), className)}
      {...rest}
    />
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

/** Multiline input (replaces antd Input.TextArea). */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { invalid, className, rows = 4, ...rest },
  ref,
) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      className={cn(
        'w-full px-3 py-2 text-sm text-content placeholder:text-subtle focus:outline-none',
        shell(invalid),
        className,
      )}
      {...rest}
    />
  );
});

// antd `Input.TextArea` — accepts the extra antd props (autoSize/showCount) and maps to Textarea.
export interface AntdTextAreaProps extends TextareaProps {
  autoSize?: boolean | { minRows?: number; maxRows?: number };
  showCount?: boolean;
}
const InputTextArea = forwardRef<HTMLTextAreaElement, AntdTextAreaProps>(function InputTextArea(
  { autoSize, showCount, rows, ...rest }, ref,
) {
  void showCount;
  const r = typeof autoSize === 'object' && autoSize?.minRows ? autoSize.minRows : rows ?? 4;
  return <Textarea ref={ref} rows={r} {...rest} />;
});

// antd `Input.Search` — an input with a leading search icon, optional clear, and
// onSearch on Enter / search-button click.
export interface AntdSearchProps extends InputProps {
  onSearch?: (value: string) => void;
  enterButton?: ReactNode | boolean;
  loading?: boolean;
}
const InputSearch = forwardRef<HTMLInputElement, AntdSearchProps>(function InputSearch(
  { onSearch, enterButton, loading, allowClear, value, onChange, onKeyDown, className, ...rest }, ref,
) {
  void loading; void enterButton;
  const clear = () => onChange?.({ target: { value: '' } } as any);
  return (
    <InputBase
      ref={ref}
      value={value}
      onChange={onChange}
      prefix={<SearchIcon className="size-4" />}
      suffix={
        allowClear && value ? (
          <button type="button" aria-label="Clear" onClick={clear} className="grid size-5 place-items-center rounded-full text-subtle hover:bg-surface-2 hover:text-content">
            <X className="size-3.5" />
          </button>
        ) : undefined
      }
      onKeyDown={(e) => { onKeyDown?.(e); if (e.key === 'Enter') onSearch?.(String((e.target as HTMLInputElement).value)); }}
      className={className}
      {...rest}
    />
  );
});

/** Exported as `Input`, with `Input.TextArea` + `Input.Search` (antd-compatible statics). */
export const Input = Object.assign(InputBase, { TextArea: InputTextArea, Search: InputSearch });
